import { authFetch } from '@/lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Edit3, Save, X, Plus, Trash2, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GuideHaccpPage() {
    const navigate = useNavigate();
    const isAdmin = JSON.parse(localStorage.getItem('guide_user') || '{}').role === 'admin';
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editSections, setEditSections] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchPage = useCallback(async () => {
        try {
            const res = await authFetch(`${API}/api/guide/pages/haccp`);
            const data = await res.json();
            setPage(data);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPage(); }, [fetchPage]);

    const sections = page?.sections || [];

    const startEdit = () => {
        setEditSections(JSON.parse(JSON.stringify(sections)));
        setEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await authFetch(`${API}/api/guide/pages/haccp`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sections: editSections }),
            });
            const data = await res.json();
            setPage(data);
            setEditing(false);
            toast.success('Page mise a jour');
        } catch { toast.error('Erreur'); }
        finally { setSaving(false); }
    };

    const addSection = () => {
        setEditSections(prev => [...prev, { id: '', title: '', items: [] }]);
    };
    const removeSection = (idx) => {
        setEditSections(prev => prev.filter((_, i) => i !== idx));
    };
    const updateSectionTitle = (idx, value) => {
        setEditSections(prev => { const c = [...prev]; c[idx] = { ...c[idx], title: value }; return c; });
    };
    const moveSection = (idx, dir) => {
        setEditSections(prev => {
            const c = [...prev];
            const target = idx + dir;
            if (target < 0 || target >= c.length) return c;
            [c[idx], c[target]] = [c[target], c[idx]];
            return c;
        });
    };
    const addItem = (sIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items.push('');
            return c;
        });
    };
    const updateItem = (sIdx, iIdx, value) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items[iIdx] = value;
            return c;
        });
    };
    const removeItem = (sIdx, iIdx) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            c[sIdx].items.splice(iIdx, 1);
            return c;
        });
    };
    const moveItem = (sIdx, iIdx, dir) => {
        setEditSections(prev => {
            const c = JSON.parse(JSON.stringify(prev));
            const target = iIdx + dir;
            if (target < 0 || target >= c[sIdx].items.length) return c;
            [c[sIdx].items[iIdx], c[sIdx].items[target]] = [c[sIdx].items[target], c[sIdx].items[iIdx]];
            return c;
        });
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

    if (editing) {
        return (
            <div className="px-4 py-5" data-testid="guide-haccp-edit">
                <h2 className="text-base font-bold text-slate-900 mb-3">Modifier les regles HACCP</h2>
                <div className="space-y-3">
                    {editSections.map((section, si) => (
                        <div key={si} className="bg-white rounded-xl border border-slate-200 p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <Input value={section.title} onChange={e => updateSectionTitle(si, e.target.value)} placeholder="Categorie..." className="h-8 text-sm font-semibold flex-1" />
                                <button onClick={() => moveSection(si, -1)} disabled={si === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                                <button onClick={() => moveSection(si, 1)} disabled={si === editSections.length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                                <button onClick={() => removeSection(si)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <div className="space-y-1 pl-2">
                                {(section.items || []).map((item, ii) => (
                                    <div key={ii} className="flex items-center gap-1">
                                        <Input value={item} onChange={e => updateItem(si, ii, e.target.value)} placeholder="Item..." className="h-7 text-xs flex-1" />
                                        <button onClick={() => moveItem(si, ii, -1)} disabled={ii === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                                        <button onClick={() => moveItem(si, ii, 1)} disabled={ii === (section.items || []).length - 1} className="text-slate-400 hover:text-slate-600 disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                                        <button onClick={() => removeItem(si, ii)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                ))}
                                <Button variant="ghost" size="sm" className="text-xs text-slate-400 w-full" onClick={() => addItem(si)}>
                                    <Plus className="w-3 h-3 mr-1" />Item
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full" onClick={addSection}>
                        <Plus className="w-4 h-4 mr-1" />Categorie
                    </Button>
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" className="flex-1" onClick={() => setEditing(false)}><X className="w-4 h-4 mr-1" />Annuler</Button>
                        <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleSave} disabled={saving}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Sauvegarder
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="px-4 py-5" data-testid="guide-haccp">
            <button onClick={() => navigate('/guide')} className="flex items-center gap-1 text-sm text-slate-500 mb-4 active:text-slate-700">
                <ArrowLeft className="w-4 h-4" /> Accueil
            </button>

            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-slate-900">{page?.title || 'Regles HACCP'}</h1>
                    <p className="text-sm text-slate-500">{page?.subtitle || "Regles d'hygiene et de securite alimentaire"}</p>
                </div>
                {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400 p-1" onClick={startEdit} data-testid="edit-haccp-btn">
                    <Edit3 className="w-3.5 h-3.5" />
                </Button>}
            </div>

            <div className="space-y-4">
                {sections.map((section) => (
                    <div key={section.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="bg-emerald-50 px-4 py-2.5 border-b border-emerald-100">
                            <h2 className="font-semibold text-emerald-800 text-sm">{section.title}</h2>
                        </div>
                        <ul className="divide-y divide-slate-100">
                            {(section.items || []).map((item, j) => (
                                <li key={j} className="px-4 py-1.5 text-sm text-slate-600 flex gap-2">
                                    <span className="text-emerald-500 font-bold mt-0.5 flex-shrink-0">-</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                {sections.length === 0 && (
                    <p className="text-sm text-slate-400 text-center py-8">Aucun contenu. Cliquez sur Modifier pour ajouter du contenu.</p>
                )}
            </div>
        </div>
    );
}
